import { AnimatePresence, motion, useMotionTemplate, useScroll, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import getPlatform from '../../../common/utils/getPlatform';
import AppDownloadModal2 from '../../AppDownloadModal2';
import { BrandSitesV2Theme } from '../common/GlobalStyle';
import useBreakpoints from '../hooks/useBreakpoints';

interface ObjectPosition {
  desktop: string;
  tablet: string;
  mobile: string;
}

const IMAGE_COUNT = 4;

const objectPositions: ObjectPosition[] = [
  {
    desktop: '50% 63%',
    tablet: '50% 42%',
    mobile: '50% 73%',
  },
  {
    desktop: '100% 44%',
    tablet: '50% 66%',
    mobile: '50% 63%',
  },
  {
    desktop: '100% 82%',
    tablet: '50% 60%',
    mobile: '50% 67%',
  },
  {
    desktop: '100% 37%',
    tablet: '50% 42%',
    mobile: '50% 68%',
  },
  {
    desktop: '100% 55%',
    tablet: '50% 32%',
    mobile: '50% 73%',
  },
];

export default function HeroSection() {
  const [imageIndex, setImageIndex] = useState(1);
  const [isAppDownloadModalVisible, setIsAppDownloadModalVisible] = useState(false);
  const media = useBreakpoints({ defaultMedia: 'unknown' });

  const nextImage = () => {
    if (document.visibilityState !== 'visible') {
      setTimeout(nextImage, 3000);
      return;
    }

    if (imageIndex === IMAGE_COUNT) setImageIndex(1);
    else setImageIndex(imageIndex + 1);
  };

  useEffect(() => {
    setTimeout(nextImage, 3000);
  }, [imageIndex]);

  useEffect(() => {
    // 이미지 프리로드
    if (media === 'unknown') return;

    for (let i = 1; i <= IMAGE_COUNT; i++) {
      const image = new Image();
      image.src = `https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${i}_${media}.webp`;
    }
  }, [media]);

  const ref = useRef<HTMLImageElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useSpring(useTransform(scrollYProgress, [0.6, 1], [0, 10]), {
    stiffness: 600,
    damping: 100,
  });
  const transform = useMotionTemplate`translateY(${y}%)`;

  return (
    <Container $imageIndex={imageIndex}>
      <HeroImageContainer>
        <HeroImageWrapper ref={ref} style={{ transform }}>
          <AnimatePresence initial={true}>
            <picture key={imageIndex}>
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${imageIndex}_mobile.webp`}
                type="image/webp"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.mobile})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${imageIndex}_tablet.webp`}
                type="image/webp"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.tablet})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${imageIndex}_desktop.webp`}
                type="image/webp"
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_mobile.png`}
                type="image/png"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.mobile})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_tablet.png`}
                type="image/png"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.tablet})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_desktop.png`}
                type="image/png"
              />
              <HeroImage
                src={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_desktop.png`}
                alt=""
                initial={{ scale: 1, opacity: 0 }}
                animate={{
                  scale: [1, 1.04],
                  opacity: [0, 1],
                  transition: { duration: 1, times: [0, 1], ease: [0.65, 0, 0.35, 1] },
                }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: 0.8,
                    times: [0, 1],
                    ease: [0.65, 0, 0.35, 1],
                  },
                }}
                key={imageIndex}
                $imageIndex={imageIndex}
              />
            </picture>
          </AnimatePresence>
        </HeroImageWrapper>
      </HeroImageContainer>
      <HeroFrontWrapper>
        <HeroFront>
          <HeroTitleWrapper
            initial={{ opacity: 0 }}
            animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
            transition={{ duration: 1, times: [0, 0.6, 1], ease: [0.65, 0, 0.35, 1] }}
          >
            <FakeHeroTitle>일상에 도움이 필요한 순간, 청연</FakeHeroTitle>
            <HeroTitleDesktop>{HeroTitleDesktopSvg}</HeroTitleDesktop>
            <HeroTitleTablet>{HeroTitleTabletSvg}</HeroTitleTablet>
          </HeroTitleWrapper>
          <HeroBottom
            initial={{ opacity: 0 }}
            animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
            transition={{ duration: 1.2, times: [0, 0.66, 1], ease: [0.65, 0, 0.35, 1] }}
          >
            <Description>청연이 함께하면 일상이 가벼워져요.</Description>
            <Link href="https://res.cleaninglab.co.kr/u/web_to_app.html" passHref>
              <Button
                onClick={(e) => {
                  const platform = getPlatform();

                  if (platform === 'mobile' || platform === 'tablet') return;

                  e.preventDefault();
                  setIsAppDownloadModalVisible(true);
                }}
              >
                앱 다운로드
              </Button>
            </Link>
          </HeroBottom>
        </HeroFront>
      </HeroFrontWrapper>
      <AppDownloadModal2
        visible={isAppDownloadModalVisible}
        setVisible={setIsAppDownloadModalVisible}
        hasBodyScrollLock
      />
    </Container>
  );
}

const HeroImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const HeroImageWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const HeroImage = styled(motion.img)<{ $imageIndex: number }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  object-fit: cover;

  object-position: ${({ $imageIndex }) => objectPositions[$imageIndex - 1]?.desktop || 'center'};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    object-position: ${({ $imageIndex }) => objectPositions[$imageIndex - 1]?.tablet || 'center'};
    transform-origin: left bottom;
    transform-origin: center center;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    transform-origin: center center;
    object-position: ${({ $imageIndex }) => objectPositions[$imageIndex - 1]?.mobile || 'center'};
  }
`;

const HeroFrontWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0 24px;
`;

const HeroFront = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.desktop};
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.tablet};
  }
`;

const HeroTitleWrapper = styled(motion.div)`
  margin-top: 174px;
  margin-bottom: 40px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-top: 124px;
    margin-bottom: 30px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-top: 114px;
    margin-bottom: 24px;
  }

  svg path {
    transition: 0.8s;
  }
`;

const FakeHeroTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const HeroTitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const HeroTitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const HeroBottom = styled(motion.div)``;

const Description = styled.p`
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  word-break: keep-all;
  max-width: 340px;
  margin-bottom: 40px;
  transition: 0.8s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    margin-bottom: 48px;
    max-width: 280px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-weight: 400;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #42484c;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: #ffffff;
    background: #42484c;
  }
`;

const Container = styled.section<{ $imageIndex: number }>`
  position: relative;
  width: 100%;
  height: 700px;
  max-height: 100vh;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    height: 620px;
  }

  /* ${({ $imageIndex }) => {
    if ($imageIndex === 3) {
      return css`
        ${HeroTitleWrapper} svg path {
          fill: #ffffff;
        }

        ${Description} {
          color: #ffffff;
        }

        ${Button} {
          color: #ffffff;
          border: 1px solid #ffffff;

          &:hover {
            transition: 0.8s;
            background-color: #ffffff;
            color: rgba(0, 0, 0, 0.6);
            border: 1px solid #ffffff;
          }
        }
      `;
    }
  }} */
`;

const HeroTitleDesktopSvg = (
  <svg width="362" height="123" viewBox="0 0 362 123" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.996 13.624C0.996 6.982 6.45 1.852 14.064 1.852C21.84 1.852 27.402 6.982 27.402 13.624C27.402 20.266 21.84 25.396 14.064 25.396C6.45 25.396 0.996 20.266 0.996 13.624ZM7.53 13.57C7.53 17.242 10.338 19.726 14.064 19.726C17.898 19.726 20.868 17.242 20.868 13.57C20.868 9.952 17.898 7.468 14.064 7.468C10.338 7.468 7.53 9.952 7.53 13.57ZM41.658 25.882H35.016V0.609999H41.658V25.882ZM42.414 51.532H12.012V37.33H35.124V33.928H12.012V28.366H41.658V42.892H18.546V46.024H42.414V51.532ZM76.2678 21.778L72.7038 26.8C68.2218 24.964 63.7398 20.806 61.7418 16.486C59.9598 20.914 55.5858 25.774 50.4018 28.204L46.4598 23.452C52.8858 20.266 58.0158 13.948 58.0158 6.28V1.852H64.9818V6.172C64.9818 13.516 70.4898 19.78 76.2678 21.778ZM80.5878 40.138C80.5878 36.682 77.6178 34.252 73.5678 34.252C69.5178 34.252 66.7098 36.682 66.7098 40.138C66.7098 43.702 69.5178 46.078 73.5678 46.078C77.6178 46.078 80.5878 43.702 80.5878 40.138ZM87.1218 40.138C87.1218 47.104 81.4518 51.586 73.5678 51.586C65.7918 51.586 60.1218 47.104 60.1218 40.138C60.1218 33.604 65.7918 28.744 73.5678 28.744C81.4518 28.744 87.1218 33.604 87.1218 40.138ZM94.7898 17.35H86.8518V28.15H80.2638V0.609999H86.8518V11.788H94.7898V17.35ZM102.832 17.458C102.832 22.102 105.262 24.964 108.556 24.964C111.904 24.964 114.388 22.102 114.388 17.458C114.388 12.868 111.904 10.006 108.556 10.006C105.262 10.006 102.832 12.868 102.832 17.458ZM132.856 45.268V0.663998H139.39V45.268H132.856ZM96.5136 17.512C96.5136 9.52 101.536 4.174 108.556 4.174C114.658 4.174 119.248 8.17 120.382 14.38H123.406V1.312H129.616V43.432H123.406V20.05H120.49C119.518 26.638 114.82 30.85 108.556 30.85C101.536 30.85 96.5136 25.504 96.5136 17.512ZM204.2 40.57H159.164V35.062H178.388V25.882H166.346V2.932H197.234V8.44H172.988V20.266H197.504V25.882H185.03V35.062H204.2V40.57ZM238.81 45.97V39.166H221.8V45.97H238.81ZM237.946 11.68C237.946 8.278 234.706 5.848 230.278 5.848C225.742 5.848 222.61 8.278 222.61 11.68C222.61 15.028 225.688 17.458 230.278 17.458C234.706 17.458 237.946 15.028 237.946 11.68ZM244.372 11.68C244.372 19.132 237.352 22.858 230.278 22.858C223.15 22.858 216.184 19.186 216.184 11.68C216.184 4.66 222.664 0.339999 230.278 0.339999C238.108 0.339999 244.372 4.714 244.372 11.68ZM245.452 51.478H215.158V33.766H226.984V30.04H207.76V24.424H252.796V30.04H233.626V33.766H245.452V51.478ZM257.544 17.296C257.544 8.98 263.268 3.472 271.098 3.472C279.036 3.472 284.706 8.98 284.706 17.296C284.706 25.558 279.036 31.066 271.098 31.066C263.268 31.066 257.544 25.558 257.544 17.296ZM264.132 17.242C264.132 22.156 267.102 25.18 271.098 25.18C275.148 25.18 278.064 22.156 278.064 17.242C278.064 12.382 275.148 9.304 271.098 9.304C267.102 9.304 264.132 12.382 264.132 17.242ZM291.564 45.214V0.663998H298.098V45.214H291.564ZM27.726 78.008H1.59V72.608H27.726V78.008ZM41.658 96.26H35.07V70.61H41.658V96.26ZM29.832 89.726L30.372 94.91C21.246 96.152 11.094 96.476 0.78 96.476L0.24 90.968C2.184 91.022 4.236 91.022 6.342 91.022L5.424 81.032L11.634 80.33L12.336 90.914L17.034 90.752L17.79 80.384L23.892 80.762L23.028 90.374C25.512 90.212 27.78 89.996 29.832 89.726ZM42.468 121.532H12.012V107.546H35.124V104.306H12.012V98.744H41.658V113.054H18.654V116.024H42.468V121.532ZM62.0118 84.434C62.0118 88.754 65.6838 91.562 70.4358 91.562C75.1878 91.562 78.9138 88.754 78.9138 84.434C78.9138 80.222 75.1878 77.306 70.4358 77.306C65.6838 77.306 62.0118 80.222 62.0118 84.434ZM92.9538 110.678H47.9178V105.116H59.2038V97.826H65.7918V105.116H75.0798V97.826H81.6678V105.116H92.9538V110.678ZM55.1538 84.434C55.1538 77.036 61.3638 71.528 70.4358 71.528C79.5078 71.528 85.7718 77.036 85.7718 84.434C85.7718 91.778 79.5078 97.232 70.4358 97.232C61.3638 97.232 55.1538 91.778 55.1538 84.434ZM136.798 120.398H106.99V106.466H113.578V114.782H136.798V120.398ZM124.972 84.596H96.5136V79.142H124.972V84.596ZM117.844 76.982H104.236V71.42H117.844V76.982ZM116.008 95.288C116.008 93.074 114.172 91.4 111.256 91.4C108.178 91.4 106.342 93.074 106.342 95.288C106.342 97.394 108.178 99.122 111.256 99.122C114.172 99.122 116.008 97.394 116.008 95.288ZM122.326 95.234C122.326 100.904 117.412 104.198 111.256 104.198C104.884 104.198 99.8616 100.904 99.8616 95.234C99.8616 89.564 104.884 86.108 111.256 86.108C117.466 86.108 122.326 89.564 122.326 95.234ZM143.386 91.022H135.448V107.87H128.752V70.664H135.448V85.46H143.386V91.022ZM166.616 105.062H173.258V114.836H197.504V120.398H166.616V105.062ZM177.848 71.42H185.516V74.39C185.516 80.762 192.698 85.514 200.258 86.594L197.666 91.994C190.322 90.968 184.22 87.026 181.682 82.058C179.198 87.026 173.042 90.86 165.698 91.994L163.106 86.594C170.612 85.514 177.848 80.762 177.848 74.39V71.42ZM185.57 110.03H179.036V100.634H159.164V95.18H204.2V100.634H185.57V110.03ZM209.812 73.256H232.924C232.546 87.89 222.556 96.692 210.46 100.472L207.328 95.18C217.804 92.48 224.176 85.622 225.526 78.764H209.812V73.256ZM248.098 119.75H218.236V102.038H224.824V114.08H248.098V119.75ZM254.632 88.538H246.694V104.63H240.052V70.664H246.694V82.976H254.632V88.538ZM257.436 114.62V107.33H265.212V113.054C265.212 117.806 262.296 121.91 257.49 122.936L256.626 119.75C259.38 119.21 261 116.834 261 114.62H257.436ZM293.045 76.172H277.493V70.61H293.045V76.172ZM305.357 111.38C305.357 108.248 302.387 106.25 298.445 106.25C294.395 106.25 291.587 108.248 291.587 111.38C291.587 114.566 294.395 116.564 298.445 116.564C302.387 116.564 305.357 114.566 305.357 111.38ZM312.323 100.58H305.735V91.13H298.013V85.73H305.735V70.556H312.323V100.58ZM298.445 84.38H289.157C289.913 89.888 294.341 92.804 299.957 94.208L297.149 99.554C292.613 98.474 287.105 94.748 285.431 91.13C283.811 94.802 278.897 98.96 273.605 100.58L270.635 95.342C275.927 93.722 280.841 90.158 281.651 84.38H272.039V78.818H298.445V84.38ZM311.891 111.38C311.891 117.806 306.221 122.072 298.445 122.072C290.669 122.072 284.999 117.806 284.999 111.38C284.999 105.17 290.669 100.742 298.445 100.742C306.221 100.742 311.891 105.17 311.891 111.38ZM319.393 85.73C319.393 78.764 324.847 73.256 332.623 73.256C336.565 73.256 339.967 74.714 342.343 77.09H354.385V70.664H360.919V105.008H354.385V93.668H342.937C340.561 96.422 336.943 98.096 332.623 98.096C324.847 98.096 319.393 92.75 319.393 85.73ZM361.621 119.75H331.381V102.038H337.969V114.08H361.621V119.75ZM325.981 85.676C325.981 89.564 328.789 92.48 332.623 92.48C336.349 92.48 339.211 89.564 339.211 85.676C339.211 81.788 336.349 78.98 332.623 78.98C328.789 78.98 325.981 81.788 325.981 85.676ZM345.637 87.998H354.385V82.814H345.529C345.745 83.786 345.853 84.758 345.853 85.73C345.853 86.54 345.799 87.296 345.637 87.998Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const HeroTitleTabletSvg = (
  <svg width="228" height="78" viewBox="0 0 228 78" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.516 8.504C0.516 4.322 3.95 1.092 8.744 1.092C13.64 1.092 17.142 4.322 17.142 8.504C17.142 12.686 13.64 15.916 8.744 15.916C3.95 15.916 0.516 12.686 0.516 8.504ZM4.63 8.47C4.63 10.782 6.398 12.346 8.744 12.346C11.158 12.346 13.028 10.782 13.028 8.47C13.028 6.192 11.158 4.628 8.744 4.628C6.398 4.628 4.63 6.192 4.63 8.47ZM26.118 16.222H21.936V0.309999H26.118V16.222ZM26.594 32.372H7.452V23.43H22.004V21.288H7.452V17.786H26.118V26.932H11.566V28.904H26.594V32.372ZM47.9093 13.638L45.6653 16.8C42.8433 15.644 40.0213 13.026 38.7633 10.306C37.6413 13.094 34.8873 16.154 31.6233 17.684L29.1413 14.692C33.1873 12.686 36.4173 8.708 36.4173 3.88V1.092H40.8033V3.812C40.8033 8.436 44.2713 12.38 47.9093 13.638ZM50.6293 25.198C50.6293 23.022 48.7593 21.492 46.2093 21.492C43.6593 21.492 41.8913 23.022 41.8913 25.198C41.8913 27.442 43.6593 28.938 46.2093 28.938C48.7593 28.938 50.6293 27.442 50.6293 25.198ZM54.7433 25.198C54.7433 29.584 51.1733 32.406 46.2093 32.406C41.3133 32.406 37.7433 29.584 37.7433 25.198C37.7433 21.084 41.3133 18.024 46.2093 18.024C51.1733 18.024 54.7433 21.084 54.7433 25.198ZM59.5713 10.85H54.5733V17.65H50.4253V0.309999H54.5733V7.348H59.5713V10.85ZM64.6347 10.918C64.6347 13.842 66.1647 15.644 68.2387 15.644C70.3467 15.644 71.9107 13.842 71.9107 10.918C71.9107 8.028 70.3467 6.226 68.2387 6.226C66.1647 6.226 64.6347 8.028 64.6347 10.918ZM83.5387 28.428V0.343999H87.6527V28.428H83.5387ZM60.6567 10.952C60.6567 5.92 63.8187 2.554 68.2387 2.554C72.0807 2.554 74.9707 5.07 75.6847 8.98H77.5887V0.751999H81.4987V27.272H77.5887V12.55H75.7527C75.1407 16.698 72.1827 19.35 68.2387 19.35C63.8187 19.35 60.6567 15.984 60.6567 10.952ZM128.459 25.47H100.103V22.002H112.207V16.222H104.625V1.772H124.073V5.24H108.807V12.686H124.243V16.222H116.389V22.002H128.459V25.47ZM150.251 28.87V24.586H139.541V28.87H150.251ZM149.707 7.28C149.707 5.138 147.667 3.608 144.879 3.608C142.023 3.608 140.051 5.138 140.051 7.28C140.051 9.388 141.989 10.918 144.879 10.918C147.667 10.918 149.707 9.388 149.707 7.28ZM153.753 7.28C153.753 11.972 149.333 14.318 144.879 14.318C140.391 14.318 136.005 12.006 136.005 7.28C136.005 2.86 140.085 0.139999 144.879 0.139999C149.809 0.139999 153.753 2.894 153.753 7.28ZM154.433 32.338H135.359V21.186H142.805V18.84H130.701V15.304H159.057V18.84H146.987V21.186H154.433V32.338ZM162.046 10.816C162.046 5.58 165.65 2.112 170.58 2.112C175.578 2.112 179.148 5.58 179.148 10.816C179.148 16.018 175.578 19.486 170.58 19.486C165.65 19.486 162.046 16.018 162.046 10.816ZM166.194 10.782C166.194 13.876 168.064 15.78 170.58 15.78C173.13 15.78 174.966 13.876 174.966 10.782C174.966 7.722 173.13 5.784 170.58 5.784C168.064 5.784 166.194 7.722 166.194 10.782ZM183.466 28.394V0.343999H187.58V28.394H183.466ZM17.346 48.968H0.89V45.568H17.346V48.968ZM26.118 60.46H21.97V44.31H26.118V60.46ZM18.672 56.346L19.012 59.61C13.266 60.392 6.874 60.596 0.38 60.596L0.0400001 57.128C1.264 57.162 2.556 57.162 3.882 57.162L3.304 50.872L7.214 50.43L7.656 57.094L10.614 56.992L11.09 50.464L14.932 50.702L14.388 56.754C15.952 56.652 17.38 56.516 18.672 56.346ZM26.628 76.372H7.452V67.566H22.004V65.526H7.452V62.024H26.118V71.034H11.634V72.904H26.628V76.372ZM38.9333 53.014C38.9333 55.734 41.2453 57.502 44.2373 57.502C47.2293 57.502 49.5753 55.734 49.5753 53.014C49.5753 50.362 47.2293 48.526 44.2373 48.526C41.2453 48.526 38.9333 50.362 38.9333 53.014ZM58.4153 69.538H30.0593V66.036H37.1653V61.446H41.3133V66.036H47.1613V61.446H51.3093V66.036H58.4153V69.538ZM34.6153 53.014C34.6153 48.356 38.5253 44.888 44.2373 44.888C49.9493 44.888 53.8933 48.356 53.8933 53.014C53.8933 57.638 49.9493 61.072 44.2373 61.072C38.5253 61.072 34.6153 57.638 34.6153 53.014ZM86.0207 75.658H67.2527V66.886H71.4007V72.122H86.0207V75.658ZM78.5747 53.116H60.6567V49.682H78.5747V53.116ZM74.0867 48.322H65.5187V44.82H74.0867V48.322ZM72.9307 59.848C72.9307 58.454 71.7747 57.4 69.9387 57.4C68.0007 57.4 66.8447 58.454 66.8447 59.848C66.8447 61.174 68.0007 62.262 69.9387 62.262C71.7747 62.262 72.9307 61.174 72.9307 59.848ZM76.9087 59.814C76.9087 63.384 73.8147 65.458 69.9387 65.458C65.9267 65.458 62.7647 63.384 62.7647 59.814C62.7647 56.244 65.9267 54.068 69.9387 54.068C73.8487 54.068 76.9087 56.244 76.9087 59.814ZM90.1687 57.162H85.1707V67.77H80.9547V44.344H85.1707V53.66H90.1687V57.162ZM104.795 66.002H108.977V72.156H124.243V75.658H104.795V66.002ZM111.867 44.82H116.695V46.69C116.695 50.702 121.217 53.694 125.977 54.374L124.345 57.774C119.721 57.128 115.879 54.646 114.281 51.518C112.717 54.646 108.841 57.06 104.217 57.774L102.585 54.374C107.311 53.694 111.867 50.702 111.867 46.69V44.82ZM116.729 69.13H112.615V63.214H100.103V59.78H128.459V63.214H116.729V69.13ZM131.993 45.976H146.545C146.307 55.19 140.017 60.732 132.401 63.112L130.429 59.78C137.025 58.08 141.037 53.762 141.887 49.444H131.993V45.976ZM156.099 75.25H137.297V64.098H141.445V71.68H156.099V75.25ZM160.213 55.598H155.215V65.73H151.033V44.344H155.215V52.096H160.213V55.598ZM161.978 72.02V67.43H166.874V71.034C166.874 74.026 165.038 76.61 162.012 77.256L161.468 75.25C163.202 74.91 164.222 73.414 164.222 72.02H161.978ZM184.399 47.812H174.607V44.31H184.399V47.812ZM192.151 69.98C192.151 68.008 190.281 66.75 187.799 66.75C185.249 66.75 183.481 68.008 183.481 69.98C183.481 71.986 185.249 73.244 187.799 73.244C190.281 73.244 192.151 71.986 192.151 69.98ZM196.537 63.18H192.389V57.23H187.527V53.83H192.389V44.276H196.537V63.18ZM187.799 52.98H181.951C182.427 56.448 185.215 58.284 188.751 59.168L186.983 62.534C184.127 61.854 180.659 59.508 179.605 57.23C178.585 59.542 175.491 62.16 172.159 63.18L170.289 59.882C173.621 58.862 176.715 56.618 177.225 52.98H171.173V49.478H187.799V52.98ZM196.265 69.98C196.265 74.026 192.695 76.712 187.799 76.712C182.903 76.712 179.333 74.026 179.333 69.98C179.333 66.07 182.903 63.282 187.799 63.282C192.695 63.282 196.265 66.07 196.265 69.98ZM200.988 53.83C200.988 49.444 204.422 45.976 209.318 45.976C211.8 45.976 213.942 46.894 215.438 48.39H223.02V44.344H227.134V65.968H223.02V58.828H215.812C214.316 60.562 212.038 61.616 209.318 61.616C204.422 61.616 200.988 58.25 200.988 53.83ZM227.576 75.25H208.536V64.098H212.684V71.68H227.576V75.25ZM205.136 53.796C205.136 56.244 206.904 58.08 209.318 58.08C211.664 58.08 213.466 56.244 213.466 53.796C213.466 51.348 211.664 49.58 209.318 49.58C206.904 49.58 205.136 51.348 205.136 53.796ZM217.512 55.258H223.02V51.994H217.444C217.58 52.606 217.648 53.218 217.648 53.83C217.648 54.34 217.614 54.816 217.512 55.258Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
